<template>
  <div class="tc-view">
    <TechnicianTopBar title="Calendario" subtitle="Tickets y mantenimiento preventivo" />

    <div v-if="loading && !loaded" class="tc-state"><span class="mdi mdi-loading tc-state__spinner"></span></div>

    <div v-else-if="blocked" class="tc-state tc-state--message">
      <span class="mdi mdi-lock-outline tc-state__icon"></span>
      <p>{{ blocked }}</p>
    </div>

    <div v-else-if="loadError" class="tc-state tc-state--message">
      <span class="mdi mdi-alert-circle-outline tc-state__icon"></span>
      <p>{{ loadError }}</p>
      <button class="tc-btn" @click="load">Reintentar</button>
    </div>

    <!-- Móvil: lista cronológica de los próximos mantenimientos -->
    <div v-else-if="isMobile" class="tc-list">
      <div class="tc-legend">
        <span v-for="l in LEGEND" :key="l.label" class="tc-legend__item">
          <i class="tc-legend__dot" :class="{ 'tc-legend__dot--outline': l.outline }" :style="l.outline ? { borderColor: l.color } : { background: l.color }"></i>{{ l.label }}
        </span>
      </div>
      <div v-if="!upcoming.length" class="tc-empty">No tienes tickets ni mantenimientos próximos.</div>
      <article v-for="item in upcoming" :key="item.kind + item.id" class="tc-card" @click="openItem(item)">
        <div class="tc-card__date" :style="{ borderColor: item.color, color: item.color }">
          <span class="tc-card__day">{{ dayNumber(item.startAt) }}</span>
          <span class="tc-card__month">{{ monthShort(item.startAt) }}</span>
        </div>
        <div class="tc-card__body">
          <div class="tc-card__title">{{ item.title }}</div>
          <div class="tc-card__meta">
            <span v-if="item.kind === 'TICKET'" class="tc-tag" :style="{ background: item.color }">{{ PRIORITY_INFO[item.priority]?.label }} · Ticket</span>
            <span v-else class="tc-tag tc-tag--outline" :style="{ color: item.color, borderColor: item.color }">{{ INFRA_LABELS[item.infraType] }}</span>
            <span v-if="item.overdue" class="tc-card__status tc-card__status--late">Vencido</span>
            <span v-if="item.zoneName || item.plotName">{{ [item.zoneName, item.plotName].filter(Boolean).join(' · ') }}</span>
          </div>
          <div class="tc-card__time">
            <template v-if="item.kind === 'SCHEDULED'">{{ timeOf(item.startAt) }}</template><template v-else>{{ item.code }}</template><template v-if="item.durationHours"> · {{ formatDuration(item.durationHours) }}</template>
            <span v-if="item.status === 'IN_PROGRESS'" class="tc-card__status">En proceso</span>
          </div>
        </div>
        <span class="mdi mdi-chevron-right tc-card__chev"></span>
      </article>
    </div>

    <!-- Escritorio: calendario mensual -->
    <div v-else class="tc-month">
      <header class="tc-month__header">
        <div class="tc-month__nav">
          <button class="tc-nav-btn" aria-label="Mes anterior" @click="shiftMonth(-1)"><span class="mdi mdi-chevron-left"></span></button>
          <span class="tc-month__title">{{ MONTH_LABELS[month - 1] }} {{ year }}</span>
          <button class="tc-nav-btn" aria-label="Mes siguiente" @click="shiftMonth(1)"><span class="mdi mdi-chevron-right"></span></button>
        </div>
        <button class="tc-today-btn" @click="goToday">Hoy</button>
      </header>

      <div class="tc-legend tc-legend--desktop">
        <span v-for="l in LEGEND" :key="l.label" class="tc-legend__item">
          <i class="tc-legend__dot" :class="{ 'tc-legend__dot--outline': l.outline }" :style="l.outline ? { borderColor: l.color } : { background: l.color }"></i>{{ l.label }}
        </span>
      </div>

      <div class="tc-weekdays"><span v-for="d in WEEKDAYS" :key="d">{{ d }}</span></div>

      <div class="tc-grid">
        <div v-for="cell in cells" :key="cell.key" class="tc-day" :class="{ 'tc-day--muted': !cell.inMonth, 'tc-day--today': cell.isToday }">
          <span class="tc-day__number" :class="{ 'tc-day__number--today': cell.isToday }">{{ cell.day }}</span>
          <div
            v-for="item in expanded.has(cell.key) ? cell.items : cell.items.slice(0, 3)"
            :key="item.kind + item.id + item.startAt"
            class="tc-pill"
            :class="{ 'tc-pill--projected': item.projected, 'tc-pill--done': item.status === 'COMPLETED', 'tc-pill--outline': item.kind === 'SCHEDULED', 'tc-pill--late': item.overdue }"
            :style="item.kind === 'SCHEDULED' ? { color: item.color, borderColor: item.color } : { background: item.color }"
            :title="item.kind === 'TICKET' ? `${item.code} · ${item.title}${item.overdue ? ' (vencido)' : ''}` : `${timeOf(item.startAt)} · ${item.title}`"
            @click="openItem(item)"
          >{{ item.title }}</div>
          <button v-if="cell.items.length > 3 && !expanded.has(cell.key)" class="tc-more" @click="expanded.add(cell.key)">
            +{{ cell.items.length - 3 }} más
          </button>
        </div>
      </div>

      <p v-if="loaded && !items.length" class="tc-month__empty">No tienes tickets ni mantenimientos este mes.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import TechnicianTopBar from '@/components/Technician/TechnicianTopBar.vue'
import { getMyCalendar, INFRA_LABELS, PRIORITY_INFO, formatDuration } from '@/services/technicianPortal'

const router = useRouter()

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

// En móvil una cuadrícula de días es difícil de usar: se muestra lista. En escritorio, calendario mensual.
const mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)') : null
const isMobile = ref(mq ? mq.matches : false)
const onMqChange = (e) => { isMobile.value = e.matches }
onMounted(() => mq?.addEventListener('change', onMqChange))
onBeforeUnmount(() => mq?.removeEventListener('change', onMqChange))

const now = new Date()
const year = ref(now.getFullYear())
const month = ref(now.getMonth() + 1)
const items = ref([])
const upcoming = ref([])
const loading = ref(true)
const loaded = ref(false)
const loadError = ref('')
const blocked = ref('')
const expanded = reactive(new Set())

// Tickets = relleno sólido por prioridad; preventivos programados = contorno con el color de su tipo de infraestructura.
const LEGEND = [
  { label: 'Crítica', color: PRIORITY_INFO.CRITICAL.color },
  { label: 'Alta', color: PRIORITY_INFO.HIGH.color },
  { label: 'Media', color: PRIORITY_INFO.MEDIUM.color },
  { label: 'Baja', color: PRIORITY_INFO.LOW.color },
  { label: 'Preventivo programado', color: '#1e3a5f', outline: true },
]

const BLOCKED_MESSAGES = {
  PORTAL_DISABLED: 'El portal de técnicos no está habilitado en este parque. Pide al administrador que lo active en Configuración.',
  TECHNICIAN_PROFILE_REQUIRED: 'Tu usuario no está vinculado a un técnico del catálogo. Pide al administrador que lo vincule.',
  TECHNICIAN_INACTIVE: 'Tu perfil de técnico está inactivo. Habla con el administrador del parque.',
}

async function load() {
  loading.value = true
  loadError.value = ''
  blocked.value = ''
  try {
    const data = await getMyCalendar(year.value, month.value)
    items.value = data.items
    upcoming.value = data.upcoming
    loaded.value = true
  } catch (err) {
    if (BLOCKED_MESSAGES[err.message]) blocked.value = BLOCKED_MESSAGES[err.message]
    else loadError.value = 'No se pudo cargar tu calendario.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch([year, month], () => { expanded.clear(); load() })

function shiftMonth(delta) {
  let m = month.value + delta
  let y = year.value
  if (m < 1) { m = 12; y -= 1 }
  if (m > 12) { m = 1; y += 1 }
  month.value = m
  year.value = y
}
function goToday() {
  const d = new Date()
  year.value = d.getFullYear()
  month.value = d.getMonth() + 1
}

const pad = (n) => String(n).padStart(2, '0')
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

// Cuadrícula de semanas completas (lunes a domingo) que cubre el mes visible.
const cells = computed(() => {
  const first = new Date(year.value, month.value - 1, 1)
  const offset = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year.value, month.value, 0).getDate()
  const total = Math.ceil((offset + daysInMonth) / 7) * 7
  const byDay = new Map()
  for (const it of items.value) {
    const key = dateKey(new Date(it.startAt))
    if (!byDay.has(key)) byDay.set(key, [])
    byDay.get(key).push(it)
  }
  const todayKey = dateKey(new Date())
  return Array.from({ length: total }, (_, i) => {
    const d = new Date(year.value, month.value - 1, 1 - offset + i)
    const key = dateKey(d)
    return {
      key,
      day: d.getDate(),
      inMonth: d.getMonth() === month.value - 1,
      isToday: key === todayKey,
      items: byDay.get(key) || [],
    }
  })
})

const dayNumber = (v) => new Date(v).getDate()
const monthShort = (v) => new Date(v).toLocaleDateString('es-MX', { month: 'short' }).replace('.', '').toUpperCase()
const timeOf = (v) => new Date(v).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })

function openItem(item) {
  router.push(item.kind === 'TICKET' ? `/tecnico/ticket/${item.id}` : `/tecnico/calendario/${item.id}`)
}
</script>

<style scoped>
.tc-view { display: flex; flex-direction: column; flex: 1; min-height: 0; background: #f8f9fc; font-size: 14px; }

.tc-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #64748b; padding: 24px; text-align: center; }
.tc-state__spinner { font-size: 1.8rem; animation: tc-spin 0.8s linear infinite; }
.tc-state__icon { font-size: 2rem; color: #94a3b8; }
.tc-state p { margin: 0; max-width: 360px; font: 400 14px Inter, sans-serif; }
@keyframes tc-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
.tc-btn { height: 36px; padding: 0 14px; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #334155; font: 500 13px Inter, sans-serif; cursor: pointer; }

/* Lista móvil */
.tc-list { flex: 1; overflow-y: auto; padding: 12px 16px 24px; display: flex; flex-direction: column; gap: 12px; }
.tc-empty { text-align: center; color: #94a3b8; font: 400 13px Inter, sans-serif; padding: 40px 0; }
.tc-card { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; cursor: pointer; }
.tc-card__date { width: 52px; height: 56px; flex-shrink: 0; border: 2px solid; border-radius: 8px; display: flex; flex-direction: column; align-items: center; justify-content: center; line-height: 1.1; }
.tc-card__day { font-family: var(--font-display); font-weight: 700; font-size: 20px; }
.tc-card__month { font: 600 10px Inter, sans-serif; letter-spacing: 0.5px; }
.tc-card__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.tc-card__title { font: 500 14px Inter, sans-serif; color: #1e3a5f; line-height: 1.3; }
.tc-card__meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font: 400 12px Inter, sans-serif; color: #64748b; }
.tc-card__time { font: 400 11px Inter, sans-serif; color: #94a3b8; display: flex; align-items: center; gap: 8px; }
.tc-card__status { font: 500 10px Inter, sans-serif; color: #b45309; background: #fef3c7; border-radius: 10px; padding: 1px 8px; }
.tc-card__chev { color: #cbd5e1; font-size: 20px; }
.tc-card__status--late { color: #b91c1c; background: #fee2e2; }
.tc-tag--outline { background: #fff; border: 1px solid; }
.tc-legend { display: flex; flex-wrap: wrap; gap: 4px 12px; font: 400 11px Inter, sans-serif; color: #64748b; }
.tc-legend--desktop { margin-bottom: 10px; }
.tc-legend__item { display: inline-flex; align-items: center; gap: 5px; }
.tc-legend__dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; box-sizing: border-box; }
.tc-legend__dot--outline { border: 2px solid; background: #fff; }
.tc-tag { color: #fff; font: 500 10px Inter, sans-serif; padding: 1px 8px; border-radius: 10px; }

/* Calendario mensual de escritorio */
.tc-month { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 16px 24px 24px; overflow-y: auto; }
.tc-month__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.tc-month__nav { display: flex; align-items: center; gap: 8px; }
.tc-month__title { font-family: var(--font-display); font-weight: 700; font-size: 16px; color: #1e3a5f; min-width: 150px; text-align: center; }
.tc-nav-btn { width: 32px; height: 32px; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; }
.tc-today-btn { height: 32px; padding: 0 14px; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #334155; font: 500 12px Inter, sans-serif; cursor: pointer; }
.tc-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 4px; }
.tc-weekdays span { text-align: center; font: 500 11px Inter, sans-serif; color: #94a3b8; }
.tc-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
.tc-day { min-height: 92px; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 4px; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.tc-day--muted { background: #f8fafc; }
.tc-day--muted .tc-day__number { color: #cbd5e1; }
.tc-day--today { border-color: #3b82f6; }
.tc-day__number { font: 500 11px Inter, sans-serif; color: #64748b; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; }
.tc-day__number--today { background: #3b82f6; color: #fff; border-radius: 50%; }
.tc-pill { color: #fff; font: 500 10px Inter, sans-serif; padding: 2px 6px; border-radius: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
.tc-pill--outline { background: #fff; border: 1px solid; padding: 1px 5px; font-weight: 600; }
.tc-pill--late { box-shadow: inset 3px 0 0 #7f1d1d; }
.tc-pill--projected { border-style: dashed; opacity: 0.75; }
.tc-pill--done { opacity: 0.55; text-decoration: line-through; }
.tc-more { background: none; border: none; padding: 0; text-align: left; cursor: pointer; font: 500 10px Inter, sans-serif; color: #64748b; }
.tc-month__empty { text-align: center; color: #94a3b8; font: 400 13px Inter, sans-serif; margin-top: 16px; }
</style>